import { api, ensureCsrfCookie } from '@/lib/api'

/**
 * @param {Record<string, unknown>} body
 * @returns {{ user?: import('@/types/api').AuthUser, two_factor_required?: boolean }}
 */
export function parseAuthResponse(body) {
  const payload = /** @type {{ data?: { user?: import('@/types/api').AuthUser, two_factor_required?: boolean } }} */ (
    body
  )

  if (payload.data?.two_factor_required) {
    return { two_factor_required: true }
  }

  if (payload.data?.user) {
    return { user: payload.data.user }
  }

  return {}
}

/**
 * @returns {Promise<import('@/types/api').AuthUser>}
 */
export async function fetchMe() {
  const { data } = await api.get('/auth/me')
  const parsed = parseAuthResponse(data)
  if (!parsed.user) {
    throw new Error('Unauthenticated')
  }
  return parsed.user
}

/**
 * @returns {Promise<import('@/types/api').AuthUser | { two_factor_required: true }>}
 */
export async function login(email, password, remember = false) {
  await ensureCsrfCookie()
  const { data } = await api.post('/auth/login', { email, password, remember })
  const parsed = parseAuthResponse(data)
  if (parsed.two_factor_required) {
    return { two_factor_required: true }
  }
  if (!parsed.user) {
    throw new Error('Login failed')
  }
  return parsed.user
}

/**
 * @param {{ code?: string, recoveryCode?: string }} params
 * @returns {Promise<import('@/types/api').AuthUser>}
 */
export async function completeTwoFactorChallenge({ code, recoveryCode }) {
  await ensureCsrfCookie()
  const payload = {}
  if (code) payload.code = code
  if (recoveryCode) payload.recovery_code = recoveryCode
  const { data } = await api.post('/auth/two-factor/challenge', payload)
  const parsed = parseAuthResponse(data)
  if (!parsed.user) {
    throw new Error('Two-factor verification failed')
  }
  return parsed.user
}

export async function logout() {
  await ensureCsrfCookie()
  await api.post('/auth/logout')
}

export async function lockScreen() {
  await ensureCsrfCookie()
  await api.post('/auth/lock')
}

/**
 * @param {string} password
 * @returns {Promise<import('@/types/api').AuthUser>}
 */
export async function unlockScreen(password) {
  await ensureCsrfCookie()
  const { data } = await api.post('/auth/unlock', { password })
  const parsed = parseAuthResponse(data)
  if (!parsed.user) {
    throw new Error('Unlock failed')
  }
  return parsed.user
}
